import { isAgentHomeProjectBoundAtom, pendingNavHrefAtom } from '@/atoms/sideNavAtoms';
import { pendingWorkHomeViewAtom } from '@heidi/agents/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import {
  isWorkChatLandingPath,
  WORK_CHAT_HOME_PATH,
  workHomeHref,
  type WorkflowSelection,
} from '@heidi/agents/contracts';
import {
  pendingWorkflowSelectionAtom,
  pendingWorkflowSelectionOriginAtom,
  workflowBrowseResetAtom,
} from '@heidi/agents/data';

interface OpenRoutinesSurfaceOptions {
  /** How the pending selection lands: 'sidebar' renders the routine standalone
   * (no Explore list); 'routines' keeps the split so the routine opens in the
   * right-hand pane beside the list. */
  origin?: 'sidebar' | 'routines';
}

/**
 * Navigate to the Routines split view, optionally selecting a workflow/run.
 * The shared cross-surface mechanism — the chat sidebar funnels through here;
 * the run chat header uses it to land back here after its routine is deleted.
 */
export function useOpenRoutinesSurface(): (
  selection: WorkflowSelection | null,
  options?: OpenRoutinesSurfaceOptions
) => void {
  const router = useRouter();
  const isProjectBound = useAtomValue(isAgentHomeProjectBoundAtom);
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);
  const setPendingView = useSetAtom(pendingWorkHomeViewAtom);
  const setPendingSelection = useSetAtom(pendingWorkflowSelectionAtom);
  const setPendingOrigin = useSetAtom(pendingWorkflowSelectionOriginAtom);
  const bumpBrowseReset = useSetAtom(workflowBrowseResetAtom);

  return useCallback(
    (selection, { origin = 'sidebar' } = {}) => {
      const onWorkLanding = isWorkChatLandingPath(router.asPath);
      const canUsePendingView = onWorkLanding && !isProjectBound;
      // A concrete selection opens that routine; `null` means "just browse" —
      // which needs the reset nonce (a null pending selection is a no-op, so it
      // can't clear an already-open routine).
      if (selection) {
        setPendingSelection(selection);
        setPendingOrigin(origin);
      } else {
        bumpBrowseReset((n) => n + 1);
        if (canUsePendingView) setPendingView('skills');
      }
      // The mounted Work page handles browse requests and writes their URL.
      // Same-path navigation needs no pending route cover.
      if (!onWorkLanding) {
        setPendingNavHref(WORK_CHAT_HOME_PATH);
      }
      if (selection || !canUsePendingView) {
        void router.push(workHomeHref({ view: 'skills', selection }), undefined, {
          shallow: true,
        });
      }
    },
    [
      router,
      isProjectBound,
      setPendingNavHref,
      setPendingView,
      setPendingSelection,
      setPendingOrigin,
      bumpBrowseReset,
    ]
  );
}
